/**
 * @fileoverview React Query Client Provider setup
 * Configures global React Query settings and provides the QueryClient to the app
 */
import React from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';

/**
 * Configuration object for React Query default options
 */
const queryClientConfig = {
  defaultOptions: {
    queries: {
      /** Time in milliseconds before data is considered stale (5 minutes) */
      staleTime: 5 * 60 * 1000,
      /** Time in milliseconds before unused data is garbage collected (10 minutes) */
      cacheTime: 10 * 60 * 1000,
      /**
       * Custom retry logic based on HTTP status codes
       * @param {number} failureCount - Number of failed attempts
       * @param {Error} error - The error object from the failed request
       * @returns {boolean} Whether to retry the request
       */
      retry: (failureCount, error) => {
        const status = error.response?.status;

        if (status >= 400 && status < 500) {
          if (status === 408 || status === 429) {
            return failureCount < 3;
          }
          return false;
        }
        return failureCount < 3;
      },
      /** Disable automatic refetch when window regains focus */
      refetchOnWindowFocus: false,
    },
    mutations: {
      /** Number of retry attempts for failed mutations */
      retry: 1,
    },
  },
};

/**
 * Global React Query client instance
 * Configured with optimized defaults for the application
 */
const queryClient = new QueryClient(queryClientConfig);

/**
 * React Query Provider component
 * Wraps the application to provide React Query functionality
 *
 * @param {Object} props - Component properties
 * @param {React.ReactNode} props.children - Child components to wrap
 * @returns {JSX.Element} QueryClientProvider with optional dev tools
 */
const AppQueryClientProvider = ({ children }) => {
  return (
    <QueryClientProvider client={queryClient}>{children}</QueryClientProvider>
  );
};

export default AppQueryClientProvider;
export { queryClient };
